/**
 * Stateless mock-API service for the Master Lot Builder demo.
 *
 * Shape-mirrors v1 endpoints (per UCG-1905 production feasibility audit):
 *
 *   - fetchRefiningPartners()          -> /refinery/refining-partners
 *   - fetchUpstreamRefiningContracts() -> /refinery/upstream-refining-contracts
 *                                         (each contract is joined with its
 *                                          partner inline; the v1 API is
 *                                          expected to do the same)
 *   - createMasterLot()                -> POST /refinery/master-lots
 *   - updateMasterLotComposition()     -> PATCH /refinery/master-lots/:id
 *   - confirmMasterLot() / unconfirmMasterLot()
 *   - updateMasterLotMetadata()
 *   - createSalesOrder() / fulfillSalesOrder()
 *                                       -> named prototype adapter seams
 *   - advanceMilestone() / setMilestoneEta() / setMilestoneNote()
 *                                       -> POST /refinery/master-lots/:id/milestones/...
 *   - cancelMasterLot()                -> POST /refinery/master-lots/:id/cancel
 *   - seedDemoData()                   -> demo-only; returns fixture array
 *
 * Source-lot data is NOT in this module. Settled source lots come from a real
 * admin-guarded endpoint (`GET /refinery/upstream-source-lots`); the vuex
 * store invokes it directly. See `store/modules/upstreamMasterLots.js`.
 *
 * State principle: this module is **stateless**. Vuex (with vuex-persistedstate)
 * owns the persisted master-lot map; mock-API mutators take the current lot,
 * compute the next state, and return it. When v1 backend lands, each method
 * here gets swapped for a real `request.get/post/...` call against the
 * matching endpoint; Vuex action signatures stay unchanged.
 */

import cloneDeep from 'lodash/cloneDeep'

import {prefixedId} from '@/util/prefixedId'
import {
  CONTRACT_HISTORY_KINDS,
  DISPOSITION_MODES,
  MASTER_LOT_STATUSES,
  MILESTONE_KINDS,
  MILESTONE_STATUSES,
  UNIT_KINDS,
} from '@/util/refinery/upstream/constants'
import {
  CHANGE_CONTRACT_ALLOWED_STATUSES,
  buildAssayOptInMapFromSettlement,
  canChangeMasterLotContract,
  computeContractSwitchPreview,
  formatContractDraftErrors,
  manifestFingerprint,
  removeManifestRowsByKey,
  resolveAssayFeeFromContract,
  resolveOtherFeesWithLegacy,
  sanitizeAssayFeeMap,
  validateContractDraft,
} from '@/util/refinery/upstream/upstreamContract'
import {
  applySettledEta,
  archiveActiveSalesOrder,
  assessContractReadiness,
  buildDemoSalesOrderIdentity,
  buildManifestSummaryForVariance,
  buildMasterLotSummarySnapshot,
  computeSettlementPreview,
  computeSettlementTotals,
  isCashInvoiceDisposition,
  netsuiteSalesOrderLinesFingerprint,
  NETSUITE_SO_STATUSES,
  projectMasterLotManifestToNetsuiteSalesOrderLines,
  requireActiveSalesOrder,
  resolveMaxSettlementBusinessDays,
  settlementEtaFromReceived,
  validateSettlementInput,
} from '@/util/refinery/upstream/upstreamMasterLot'
import {normalizeLotInventoryLocation} from '@/util/refinery/upstream/upstreamMasterLot/catalogProductInventory'
import {catalogEntriesFromManifest} from '@/util/refinery/upstream/upstreamMasterLot/catalogItems'

import {buildDemoMasterLotFixtures} from './upstreamMasterLotDemoFixtures'
import {
  REFINING_PARTNERS,
  UPSTREAM_REFINING_CONTRACTS,
  generateMockMasterLotCode,
} from './upstreamMasterLotMockData'

const DEFAULT_LATENCY_MS = 350

const delay = (value, ms = DEFAULT_LATENCY_MS) =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms))

// Mirror of `delay`'s shape on the failure path: lets validation paths bail
// with the same simulated network latency they'd get on success, without
// repeating a four-line `new Promise(...setTimeout...reject)` block per
// guard clause.
const rejectLater = (message, latencyMs) =>
  new Promise((_, reject) => setTimeout(() => reject(new Error(message)), latencyMs ?? DEFAULT_LATENCY_MS))

// Inline partner-join, deep-cloned so callers can't mutate the source array.
// Mirrors the join the v1 API is expected to do server-side.
const resolveJoinedPartner = (partnerId) => {
  const partner = REFINING_PARTNERS.find((candidate) => candidate.id === partnerId) || null
  return partner ? cloneDeep(partner) : null
}

export const fetchRefiningPartners = ({latencyMs} = {}) => {
  return delay(cloneDeep(REFINING_PARTNERS), latencyMs ?? DEFAULT_LATENCY_MS)
}

/**
 * Returns each contract joined with its partner inline (`contract.partner`).
 * The v1 API is expected to do the same join server-side for ergonomics so
 * the FE never has to chain `refiningPartnerId → /refining-partners/:id`.
 */
export const fetchUpstreamRefiningContracts = ({latencyMs} = {}) => {
  const contracts = UPSTREAM_REFINING_CONTRACTS.map((contract) => ({
    ...cloneDeep(contract),
    partner: resolveJoinedPartner(contract.refiningPartnerId),
  }))
  return delay(contracts, latencyMs ?? DEFAULT_LATENCY_MS)
}

/**
 * Submit a new master lot. The mock server validates lightly (must have at
 * least one unit, each catalog entry must have positive weight + purity) and
 * returns a synthesised confirmation with summary stats and a milestone
 * checklist seeded from the contract's expected milestones.
 *
 * `nextCode` is provided by the caller (Vuex computes it from existing state
 * to avoid sequence collisions across page reloads / persistence rehydration);
 * if omitted, falls back to the legacy in-module counter.
 */
export const createMasterLot = (payload, {latencyMs, nextCode} = {}) => {
  const errors = validateCreateMasterLotPayload(payload)
  if (errors.length > 0) return rejectLater(errors.join('; '), latencyMs)

  const {contract, selectedUnits = [], catalogEntries = [], inventoryLocation} = payload
  const allUnits = [...selectedUnits, ...catalogEntries]
  const masterLotCode = nextCode || generateMockMasterLotCode()
  const sourceLotCount = countDistinctSourceLots(selectedUnits)
  const nowIso = new Date().toISOString()

  const result = {
    id: masterLotCode,
    masterLotCode,
    status: MASTER_LOT_STATUSES.DRAFT,
    createdAt: nowIso,
    updatedAt: nowIso,
    inventoryLocation: normalizeLotInventoryLocation(inventoryLocation),
    contract: cloneDeep(contract),
    summary: buildMasterLotSummarySnapshot({
      allUnits,
      sourceUnitCount: selectedUnits.length,
      catalogEntryCount: catalogEntries.length,
      sourceLotCount,
    }),
    manifest: {
      sourceUnits: cloneDeep(selectedUnits),
      catalogEntries: cloneDeep(catalogEntries),
    },
    milestones: (contract?.expectedMilestones || []).map((milestone) => ({
      ...milestone,
      expectedAt: null,
      occurredAt: null,
      recordedByUserId: null,
      recordedByName: null,
      note: null,
      status: MILESTONE_STATUSES.PENDING,
    })),
    notes: payload.notes || '',
  }
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

/**
 * Recompute composition + summary on an existing master lot. Used by the
 * edit view to swap source units / catalog entries / contract while the
 * lot is still in `draft` status. Status, milestones, and code are preserved.
 *
 * Returns a fresh master lot object (immutable update).
 */
export const updateMasterLotComposition = (masterLot, payload, {latencyMs} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (masterLot.status !== MASTER_LOT_STATUSES.DRAFT) {
    return rejectLater(
      `composition is locked once status leaves draft (currently: ${masterLot.status})`,
      latencyMs,
    )
  }
  const errors = validateCreateMasterLotPayload(payload, {allowEmpty: true})
  if (errors.length > 0) return rejectLater(errors.join('; '), latencyMs)
  const {contract, selectedUnits = [], catalogEntries = [], notes, inventoryLocation} = payload
  const contractChanged = masterLot.contract?.id !== contract?.id
  const reviewedSwitch = contractChanged
    ? validateCompositionContractSwitch(masterLot, payload)
    : null
  if (reviewedSwitch?.error) return rejectLater(reviewedSwitch.error, latencyMs)
  const allUnits = [...selectedUnits, ...catalogEntries]
  const sourceLotCount = countDistinctSourceLots(selectedUnits)
  const contractHistory = reviewedSwitch
    ? [
        ...(Array.isArray(masterLot.contractHistory) ? cloneDeep(masterLot.contractHistory) : []),
        buildContractHistoryEntry(masterLot.contract, contract, {
          notes: payload.contractSwitchReview?.notes,
          manifestResolution: reviewedSwitch.manifestResolution,
        }),
      ]
    : cloneDeep(masterLot.contractHistory)
  const result = {
    ...cloneDeep(masterLot),
    inventoryLocation: inventoryLocation !== undefined
      ? normalizeLotInventoryLocation(inventoryLocation)
      : normalizeLotInventoryLocation(masterLot.inventoryLocation),
    contract: cloneDeep(contract),
    manifest: {
      sourceUnits: cloneDeep(selectedUnits),
      catalogEntries: cloneDeep(catalogEntries),
    },
    summary: buildMasterLotSummarySnapshot({
      allUnits,
      sourceUnitCount: selectedUnits.length,
      catalogEntryCount: catalogEntries.length,
      sourceLotCount,
    }),
    ...(contractHistory !== undefined ? {contractHistory} : {}),
    notes: notes !== undefined ? notes : masterLot.notes || '',
    updatedAt: new Date().toISOString(),
  }
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

const validateCompositionContractSwitch = (masterLot, payload) => {
  const review = payload.contractSwitchReview
  if (!review || typeof review !== 'object') {
    return {error: 'a reviewed contract switch is required before changing composition contract'}
  }
  if (review.fromContractId !== masterLot.contract?.id) {
    return {error: 'contract switch review no longer matches the persisted contract'}
  }
  if (review.newContractId !== payload.contract?.id) {
    return {error: 'contract switch review does not match the selected contract'}
  }
  const reviewedManifest = review.reviewedManifest
  const previewLot = {...masterLot, manifest: reviewedManifest}
  const preview = computeContractSwitchPreview(previewLot, payload.contract)
  const resolution = resolveManifestResolution(preview, review.removeManifestRowKeys)
  if (resolution.error) return resolution
  const expectedManifest = removeManifestRowsByKey(reviewedManifest, resolution.removedRowKeys)
  const submittedManifest = {
    sourceUnits: payload.selectedUnits || [],
    catalogEntries: payload.catalogEntries || [],
  }
  if (manifestFingerprint(expectedManifest) !== manifestFingerprint(submittedManifest)) {
    return {error: 'composition changed after contract review; review the contract switch again'}
  }
  return {
    manifestResolution: resolution,
  }
}

const buildContractHistoryEntry = (
  fromContract,
  toContract,
  {notes, manifestResolution, recordedAt, confirmationInvalidated = false} = {},
) => ({
  at: recordedAt || new Date().toISOString(),
  kind: fromContract?.id === toContract?.id
    ? CONTRACT_HISTORY_KINDS.RE_SNAPSHOT
    : CONTRACT_HISTORY_KINDS.SWITCH,
  fromContractId: fromContract?.id || null,
  fromContractTitle: fromContract?.title || null,
  toContractId: toContract?.id,
  toContractTitle: toContract?.title || null,
  manifestResolution,
  ...(confirmationInvalidated ? {confirmationInvalidated: true} : {}),
  ...(notes && String(notes).trim() ? {notes: String(notes).trim()} : {}),
})

export const createSalesOrder = (
  request = {},
  {latencyMs, netsuiteAppUrl} = {},
) => {
  if (!request || typeof request !== 'object') {
    return rejectLater('master lot required', latencyMs)
  }
  const {masterLotId, masterLotCode, lines} = request
  if (!masterLotId) return rejectLater('master lot required', latencyMs)
  if (!netsuiteAppUrl) return rejectLater('NetSuite app URL required for sales order creation', latencyMs)
  if (!Array.isArray(lines) || lines.length === 0) {
    return rejectLater('projected sales order lines required', latencyMs)
  }
  try {
    assertProjectedSalesOrderLines(lines)
  } catch (error) {
    return rejectLater(error.message, latencyMs)
  }
  return delay(
    buildDemoSalesOrderIdentity(masterLotCode || masterLotId, netsuiteAppUrl),
    latencyMs ?? DEFAULT_LATENCY_MS,
  )
}

/**
 * Atomic confirmation boundary: no lot state is returned until the simulated
 * SO adapter succeeds, so callers cannot persist a partial flip.
 */
export const confirmMasterLot = async (
  masterLot,
  {latencyMs, netsuiteAppUrl, salesOrderAdapter = createSalesOrder} = {},
) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (masterLot.status !== MASTER_LOT_STATUSES.DRAFT) {
    return rejectLater(`only draft lots can be confirmed (currently: ${masterLot.status})`, latencyMs)
  }
  const manifestUnits = [
    ...(masterLot.manifest?.sourceUnits || []),
    ...(masterLot.manifest?.catalogEntries || []),
  ]
  const readiness = assessContractReadiness(manifestUnits, masterLot.contract)
  if (!readiness.canConfirm) {
    return rejectLater(
      `cannot confirm: ${readiness.blockers.map((blocker) => blocker.message).join(' ')}`,
      latencyMs,
    )
  }
  let lines
  try {
    lines = projectMasterLotManifestToNetsuiteSalesOrderLines(masterLot)
    assertProjectedSalesOrderLines(lines)
  } catch (error) {
    return rejectLater(error.message, latencyMs)
  }
  if (lines.length === 0) {
    return rejectLater('cannot confirm: no projected sales order lines', latencyMs)
  }
  const salesOrder = await salesOrderAdapter({
    masterLotId: masterLot.id,
    masterLotCode: masterLot.masterLotCode || masterLot.id,
    lines,
  }, {latencyMs, netsuiteAppUrl})
  try {
    requireActiveSalesOrder({salesOrder})
  } catch (error) {
    return rejectLater(error.message, latencyMs)
  }
  const sentAt = new Date().toISOString()
  const outbound = {
    sentAt,
    fingerprint: netsuiteSalesOrderLinesFingerprint(lines),
    lineCount: lines.length,
    salesOrder: cloneDeep(salesOrder),
    lines: cloneDeep(lines),
  }
  const priorSalesOrderSync = cloneDeep(masterLot.netsuiteSync?.salesOrder || {})
  return {
    ...cloneDeep(masterLot),
    salesOrder: cloneDeep(salesOrder),
    readinessWarnings: cloneDeep(readiness.warnings),
    status: MASTER_LOT_STATUSES.CONFIRMED,
    netsuiteSync: {
      ...(cloneDeep(masterLot.netsuiteSync) || {}),
      salesOrder: {
        ...priorSalesOrderSync,
        lastOutbound: outbound,
        outboundHistory: [...(priorSalesOrderSync.outboundHistory || []), outbound],
      },
    },
    updatedAt: sentAt,
  }
}

const assertProjectedSalesOrderLines = (lines) => {
  const seenSourceSkus = new Set()
  for (const line of lines) {
    if (!line?.componentKey || !line?.kind || !line?.sku) {
      throw new Error('malformed projected sales order line')
    }
    if (!(Number(line.quantity) > 0)) {
      throw new Error(`projected line quantity must be positive for ${line.componentKey}`)
    }
    if (line.kind === 'source') {
      if (line.item !== undefined) {
        throw new Error(`source projected line must omit item for ${line.componentKey}`)
      }
      if (seenSourceSkus.has(line.sku)) {
        throw new Error(`duplicate current source SKU: ${line.sku}`)
      }
      seenSourceSkus.add(line.sku)
      continue
    }
    if (line.kind !== 'catalog') throw new Error(`unsupported projected line kind: ${line.kind}`)
    if (!/^\d+$/.test(String(line.item?.id || ''))) {
      throw new Error(`numeric NetSuite item id required for ${line.componentKey}`)
    }
  }
}

/** Confirmed-lot metadata path; composition remains locked. */
export const updateMasterLotMetadata = (masterLot, {notes} = {}, {latencyMs} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (masterLot.status !== MASTER_LOT_STATUSES.CONFIRMED) {
    return rejectLater(`metadata update requires a confirmed lot (currently: ${masterLot.status})`, latencyMs)
  }
  if (notes !== undefined && typeof notes !== 'string') {
    return rejectLater('notes must be a string', latencyMs)
  }
  return delay({
    ...cloneDeep(masterLot),
    notes: notes !== undefined ? notes : masterLot.notes || '',
    updatedAt: new Date().toISOString(),
  }, latencyMs ?? DEFAULT_LATENCY_MS)
}

/** @experimental reopen composition before shipment and void the simulated SO. */
export const unconfirmMasterLot = (masterLot, {latencyMs} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (masterLot.status !== MASTER_LOT_STATUSES.CONFIRMED) {
    return rejectLater(`only confirmed lots can be unconfirmed (currently: ${masterLot.status})`, latencyMs)
  }
  const archived = archiveActiveSalesOrder(masterLot, {
    reason: 'unconfirm',
    at: new Date().toISOString(),
  })
  return delay({
    ...archived,
    status: MASTER_LOT_STATUSES.DRAFT,
    readinessWarnings: [],
    updatedAt: new Date().toISOString(),
  }, latencyMs ?? DEFAULT_LATENCY_MS)
}

/** @experimental simulated NetSuite SO-fulfill adapter. */
export const fulfillSalesOrder = (masterLot, {latencyMs} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (masterLot.status !== MASTER_LOT_STATUSES.CONFIRMED) {
    return rejectLater(`sales order fulfillment requires a confirmed lot (currently: ${masterLot.status})`, latencyMs)
  }
  let salesOrder
  try {
    salesOrder = requireActiveSalesOrder(masterLot)
  } catch (error) {
    return rejectLater(error.message, latencyMs)
  }
  return delay({
    salesOrder: {
      ...cloneDeep(salesOrder),
      status: cloneDeep(NETSUITE_SO_STATUSES.PENDING_BILLING),
    },
    fulfilledAt: new Date().toISOString(),
  }, latencyMs ?? DEFAULT_LATENCY_MS)
}

// Conservative seed at `received` time: opt-in metals are unknown, so
// `resolveMaxSettlementBusinessDays` is called without an optInMap and treats
// every paid metal as in-scope (longest possible SLA). Recomputed at
// `recordSettlement` once the operator's opt-in decisions are known.
const seedSettledEtaFromReceived = (masterLot, receivedOccurredAtIso) => {
  const contract = masterLot && masterLot.contract
  if (!contract) return masterLot
  const businessDays = resolveMaxSettlementBusinessDays(contract, masterLot)
  if (!Number.isFinite(businessDays) || businessDays <= 0) return masterLot
  return applySettledEta(masterLot, settlementEtaFromReceived(receivedOccurredAtIso, businessDays))
}

/**
 * Advance a single milestone on a master lot — flips its status from pending
 * to complete, records `occurredAt` + the recording user + optional note, and
 * bumps the lot's overall status to match the milestone kind. Lifecycle-only;
 * no composition change. Returns the updated lot.
 *
 * `kind` is the milestone kind enum value (e.g. 'shipped', 'received').
 * `occurredAt` defaults to "now" if omitted. `recordedBy*` capture WHO entered
 * the milestone (the logged-in user), not who physically performed the step.
 */
export const advanceMilestone = (masterLot, kind, {occurredAt, recordedByUserId, recordedByName, note, latencyMs} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (kind === MILESTONE_KINDS.SHIPPED && masterLot?.status !== MASTER_LOT_STATUSES.CONFIRMED) {
    return rejectLater(
      `cannot ship: lot must be confirmed (currently: ${masterLot?.status || 'missing'})`,
      latencyMs,
    )
  }
  const occurredAtIso = occurredAt || new Date().toISOString()
  return mutateMilestone(masterLot, kind, (milestone) => ({
    ...milestone,
    occurredAt: occurredAtIso,
    recordedByUserId: recordedByUserId !== undefined ? recordedByUserId : milestone.recordedByUserId,
    recordedByName: recordedByName !== undefined ? recordedByName : milestone.recordedByName,
    note: note !== undefined ? note : milestone.note,
    status: MILESTONE_STATUSES.COMPLETE,
  }), {
    bumpStatusTo: kind,
    latencyMs,
    postTransform: kind === MILESTONE_KINDS.RECEIVED
      ? (lot) => seedSettledEtaFromReceived(lot, occurredAtIso)
      : undefined,
  })
}

/** @param {{latencyMs?:number}} options */
export const setMilestoneEta = (masterLot, kind, expectedAt, {latencyMs} = {}) => {
  return mutateMilestone(masterLot, kind, (milestone) => ({...milestone, expectedAt}), {latencyMs})
}

/** @param {{latencyMs?:number}} options */
export const setMilestoneNote = (masterLot, kind, note, {latencyMs} = {}) => {
  return mutateMilestone(masterLot, kind, (milestone) => ({...milestone, note}), {latencyMs})
}

/**
 * Record the upstream refiner's settlement on an `assayed` master lot.
 *
 * Atomic at the boundary: the returned object carries the settlement payload
 * AND the bumped `settled` milestone AND `status: 'settled'` in one shot.
 * Vuex commits once. No two-call rollback plumbing required.
 */
export const recordSettlement = (masterLot, input, {latencyMs} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (masterLot.status !== MASTER_LOT_STATUSES.ASSAYED) {
    return rejectLater(
      `cannot record settlement: lot must be 'assayed' (currently: ${masterLot.status})`,
      latencyMs,
    )
  }
  const errors = validateSettlementInput(masterLot, input)
  if (errors.length > 0) return rejectLater(errors.join('; '), latencyMs)
  const recordedAt = input.recordedAt || new Date().toISOString()
  const settlement = buildSettlementPayload(masterLot, input, recordedAt)
  let milestones = (masterLot.milestones || []).map((milestone) =>
    milestone.kind === MILESTONE_KINDS.SETTLED
      ? {...cloneDeep(milestone), status: MILESTONE_STATUSES.COMPLETE, occurredAt: recordedAt}
      : cloneDeep(milestone)
  )
  const withEta = patchSettledEtaFromSettlement({...masterLot, milestones}, input)
  milestones = withEta.milestones
  const result = {
    ...cloneDeep(masterLot),
    settlement,
    milestones,
    status: MASTER_LOT_STATUSES.SETTLED,
    updatedAt: new Date().toISOString(),
  }
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

/**
 * Replace an existing settlement payload on an already-`settled` master lot.
 * Does NOT re-advance the `settled` milestone (it's already complete) and
 * does NOT touch lot status.
 */
export const updateSettlement = (masterLot, input, {latencyMs} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (masterLot.status !== MASTER_LOT_STATUSES.SETTLED) {
    return rejectLater(
      `can only update settlement on settled lots (currently: ${masterLot.status})`,
      latencyMs,
    )
  }
  const errors = validateSettlementInput(masterLot, input)
  if (errors.length > 0) return rejectLater(errors.join('; '), latencyMs)
  const recordedAt = input.recordedAt || masterLot.settlement?.recordedAt || new Date().toISOString()
  const settlement = buildSettlementPayload(masterLot, input, recordedAt)
  const result = {
    ...cloneDeep(masterLot),
    settlement,
    updatedAt: new Date().toISOString(),
  }
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

// Stateless contract-CRUD mutators: take input draft / existing contract,
// return next-state. Each method mirrors the v1 endpoint shape; persistence
// lives in the upstreamContracts vuex module.
/** @param {{latencyMs?:number}} options */
export const createContract = (draft, {latencyMs} = {}) => {
  if (!draft || typeof draft !== 'object') return rejectLater('draft required', latencyMs)
  const draftErrors = validateContractDraft(draft)
  if (draftErrors.length) return rejectLater(formatContractDraftErrors(draftErrors), latencyMs)
  const id = newContractId(draft)
  const nowIso = new Date().toISOString()
  const paidMetals = Array.isArray(draft.paidMetals) ? draft.paidMetals : []
  const result = {
    ...cloneDeep(draft),
    id,
    active: true,
    partner: resolveJoinedPartner(draft.refiningPartnerId),
    assayFee: sanitizeAssayFeeMap(draft.assayFee, paidMetals),
    createdAt: nowIso,
    updatedAt: nowIso,
  }
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

/**
 * Merge `patch` into `existing` and return the next-state contract. Preserves
 * `id`, `createdAt`, and `active` (use `setContractActive`
 * for active toggles to keep the boundary single-purpose).
 *
 * Re-resolves the joined `partner` when `refiningPartnerId` changes so the
 * client never has to re-fetch the partner list to pick up a swap.
 */
export const updateContract = (existing, patch, {latencyMs} = {}) => {
  if (!existing || !existing.id) return rejectLater('contract required', latencyMs)
  const merged = {
    ...cloneDeep(existing),
    ...cloneDeep(patch || {}),
    id: existing.id,
    createdAt: existing.createdAt,
    active: existing.active,
    updatedAt: new Date().toISOString(),
  }
  if (patch && patch.refiningPartnerId && patch.refiningPartnerId !== existing.refiningPartnerId) {
    merged.partner = resolveJoinedPartner(patch.refiningPartnerId)
  }
  merged.assayFee = sanitizeAssayFeeMap(merged.assayFee, merged.paidMetals)
  const draftErrors = validateContractDraft(merged)
  if (draftErrors.length) return rejectLater(formatContractDraftErrors(draftErrors), latencyMs)
  return delay(merged, latencyMs ?? DEFAULT_LATENCY_MS)
}

/** @param {{latencyMs?:number}} options */
export const setContractActive = (existing, active, {latencyMs} = {}) => {
  if (!existing || !existing.id) return rejectLater('contract required', latencyMs)
  const result = {
    ...cloneDeep(existing),
    active: Boolean(active),
    updatedAt: new Date().toISOString(),
  }
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

const newContractId = (draft) => {
  const partnerSlug = (draft.refiningPartnerId || 'partner').replace(/^partner-/, '') || 'partner'
  return prefixedId(`contract-${partnerSlug}`)
}

/**
 * Re-base an in-flight master lot onto another (or re-snapshot the same)
 * active contract. Deep-clones the contract snapshot rather than swapping
 * an FK so historical reads stay stable.
 *
 * Invariants:
 *   - Status gate: draft + confirmed only; shipped and later are immutable
 *     (see `CHANGE_CONTRACT_ALLOWED_STATUSES`).
 *   - Confirmed switches invalidate confirmation, clear the simulated SO,
 *     and return the lot to draft atomically.
 *   - Milestone semantics are union-preserve: existing completed /
 *     pending-with-ETA entries kept verbatim, new pending kinds appended,
 *     dropped kinds NOT deleted (audit-trail friendly).
 *   - Appends a contractHistory entry: `{at, kind: 're-snapshot' | 'switch',
 *     fromContractId, fromContractTitle, toContractId, toContractTitle,
 *     notes?, manifestResolution}`.
 */
export const changeMasterLotContract = (
  masterLot,
  newContract,
  {notes, recordedAt, removeManifestRowKeys = []} = {},
  {latencyMs} = {},
) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (!newContract || !newContract.id) return rejectLater('new contract required', latencyMs)
  if (!canChangeMasterLotContract(masterLot.status)) {
    return rejectLater(
      `cannot change contract: lot status is ${masterLot.status} (allowed: ${[...CHANGE_CONTRACT_ALLOWED_STATUSES].join(', ')})`,
      latencyMs,
    )
  }
  const preview = computeContractSwitchPreview(masterLot, newContract)
  const resolution = resolveManifestResolution(preview, removeManifestRowKeys)
  if (resolution.error) return rejectLater(resolution.error, latencyMs)
  const at = recordedAt || new Date().toISOString()
  const fromContract = masterLot.contract || {}
  const milestones = unionMilestones(masterLot.milestones, newContract.expectedMilestones)
  const manifest = resolution.removedRowKeys.length
    ? cloneDeep(removeManifestRowsByKey(masterLot.manifest, resolution.removedRowKeys))
    : cloneDeep(masterLot.manifest)
  const sourceUnits = Array.isArray(manifest?.sourceUnits) ? manifest.sourceUnits : []
  const catalogEntries = catalogEntriesFromManifest(manifest)
  const confirmationInvalidated = masterLot.status === MASTER_LOT_STATUSES.CONFIRMED
  const summary = resolution.removedRowKeys.length
    ? buildMasterLotSummarySnapshot({
        allUnits: [...sourceUnits, ...catalogEntries],
        sourceUnitCount: sourceUnits.length,
        catalogEntryCount: catalogEntries.length,
        sourceLotCount: countDistinctSourceLots(sourceUnits),
      })
    : cloneDeep(masterLot.summary)
  const historyEntry = buildContractHistoryEntry(fromContract, newContract, {
    recordedAt: at,
    notes,
    confirmationInvalidated,
    manifestResolution: resolution,
  })
  const contractHistory = [
    ...(Array.isArray(masterLot.contractHistory) ? cloneDeep(masterLot.contractHistory) : []),
    historyEntry,
  ]
  const baseResult = {
    ...cloneDeep(masterLot),
    contract: cloneDeep(newContract),
    manifest,
    summary,
    milestones,
    contractHistory,
    status: MASTER_LOT_STATUSES.DRAFT,
    ...(confirmationInvalidated ? {readinessWarnings: []} : {}),
    updatedAt: new Date().toISOString(),
  }
  const result = confirmationInvalidated
    ? archiveActiveSalesOrder(baseResult, {reason: 'contract_switch', at})
    : baseResult
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

const resolveManifestResolution = (preview, removeManifestRowKeys) => {
  if (!Array.isArray(removeManifestRowKeys)) {
    return {error: 'removeManifestRowKeys must be an array'}
  }
  const requested = removeManifestRowKeys.map((key) => String(key))
  if (new Set(requested).size !== requested.length) {
    return {error: 'removeManifestRowKeys must not contain duplicates'}
  }
  const incompatibleKeys = new Set(
    (Array.isArray(preview?.incompatibleManifestRows) ? preview.incompatibleManifestRows : [])
      .map((row) => row.key),
  )
  if (requested.some((key) => !incompatibleKeys.has(key))) {
    return {error: 'can only remove incompatible manifest rows from the reviewed contract switch'}
  }
  return {
    removedRowKeys: requested,
    preservedIncompatibleRowKeys: [...incompatibleKeys].filter((key) => !requested.includes(key)),
  }
}

/**
 * Union the lot's existing milestones with any kinds the new contract
 * introduces (as fresh pending entries). Preserves existing entries by
 * reference-clone; new entries get the same default shape `createMasterLot`
 * seeds with.
 */
const unionMilestones = (existing, newExpected) => {
  const existingArray = Array.isArray(existing) ? existing : []
  const existingKinds = new Set(existingArray.map((milestone) => milestone && milestone.kind).filter(Boolean))
  const result = existingArray.map((milestone) => cloneDeep(milestone))
  for (const expected of (Array.isArray(newExpected) ? newExpected : [])) {
    if (!expected || !expected.kind || existingKinds.has(expected.kind)) continue
    result.push({
      ...cloneDeep(expected),
      expectedAt: null,
      occurredAt: null,
      recordedByUserId: null,
      recordedByName: null,
      note: null,
      status: MILESTONE_STATUSES.PENDING,
    })
    existingKinds.add(expected.kind)
  }
  return result
}

/** @param {{latencyMs?:number}} options */
export const cancelMasterLot = (masterLot, {latencyMs} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  const result = {
    ...cloneDeep(masterLot),
    status: MASTER_LOT_STATUSES.CANCELLED,
    updatedAt: new Date().toISOString(),
  }
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

/**
 * Demo-only: returns the auto-seeded fixture lot array. Wired to
 * `bootstrapDemoIfEmpty` in the upstreamMasterLots vuex module so a stakeholder
 * hitting `/refinery/master-lots` for the first time sees realistic content.
 * Removed when v1 backend lands.
 */
export const seedDemoData = ({latencyMs, netsuiteAppUrl} = {}) => {
  return delay(buildDemoMasterLotFixtures({netsuiteAppUrl}), latencyMs ?? DEFAULT_LATENCY_MS)
}

const mutateMilestone = (masterLot, kind, transform, {bumpStatusTo, latencyMs, postTransform} = {}) => {
  if (!masterLot || !masterLot.id) return rejectLater('master lot required', latencyMs)
  if (!Array.isArray(masterLot.milestones) || !masterLot.milestones.some((milestone) => milestone.kind === kind)) {
    return rejectLater(`milestone kind not present on lot: ${kind}`, latencyMs)
  }
  const milestones = masterLot.milestones.map((milestone) => (
    milestone.kind === kind ? transform(cloneDeep(milestone)) : cloneDeep(milestone)
  ))
  let result = {
    ...cloneDeep(masterLot),
    milestones,
    updatedAt: new Date().toISOString(),
  }
  if (bumpStatusTo && masterLot.status !== MASTER_LOT_STATUSES.CANCELLED) {
    result.status = bumpStatusTo
  }
  if (typeof postTransform === 'function') {
    result = postTransform(result)
  }
  return delay(result, latencyMs ?? DEFAULT_LATENCY_MS)
}

const validateCreateMasterLotPayload = (payload, {allowEmpty = false} = {}) => {
  const errors = []
  if (!payload || typeof payload !== 'object') {
    return ['payload required']
  }
  const totalUnits =
    (payload.selectedUnits?.length || 0) + (payload.catalogEntries?.length || 0)
  if (!allowEmpty && totalUnits === 0) {
    errors.push('master lot needs at least one unit')
  }
  for (const catalogEntry of payload.catalogEntries || []) {
    if (catalogEntry?.kind !== UNIT_KINDS.CATALOG) {
      errors.push('catalog entry kind must be catalog')
    }
    if (!(Number.isInteger(Number(catalogEntry?.quantity)) && Number(catalogEntry.quantity) > 0)) {
      errors.push('catalog entry quantity must be a positive whole number')
    }
    const unitWeightToz = catalogEntry?.unitWeight !== null && catalogEntry?.unitWeight !== undefined
      ? Number(catalogEntry?.unitWeight?.troyOunces)
      : Number(catalogEntry?.unitWeightToz)
    if (!(unitWeightToz > 0)) {
      errors.push('catalog entry unit weight must be positive')
    }
    if (
      catalogEntry?.kind === UNIT_KINDS.CATALOG &&
      !(catalogEntry?.purityFraction > 0 && catalogEntry.purityFraction <= 1)
    ) {
      errors.push('catalog entry purity fraction must be between 0 and 1')
    }
    if (!(catalogEntry?.weightToz > 0)) errors.push('catalog entry weight must be positive')
    if (!(catalogEntry?.purity && Object.values(catalogEntry.purity).some((purityValue) => purityValue > 0))) {
      errors.push('catalog entry must declare at least one positive purity')
    }
    if (!catalogEntry?.sku) {
      errors.push('catalog entry requires sku')
    }
  }
  return errors
}

const countDistinctSourceLots = (units) => {
  const codes = new Set()
  for (const unit of units) {
    if (unit?.sourceLotCode) codes.add(unit.sourceLotCode)
  }
  return codes.size
}

// `validateSettlementInput` lives in `@/util/refinery/upstream/upstreamMasterLot`
// so the future v1 backend route handler can share the same rules
// without re-implementing them.

/**
 * Normalize the input adHocFees: assign ids where missing, trim labels,
 * propagate optional `note`, persist categorized `code` (drives the
 * `hasImpurityCharges` SLA modifier at settled-ETA recompute time; null
 * sentinel for pre-extension rows). Caller-supplied ids are preserved
 * verbatim so edit-mode re-submissions don't churn ids across save cycles.
 */
const normalizeAdHocFees = (rows) => {
  if (!Array.isArray(rows)) return []
  return rows.map((row) => ({
    id: row?.id || prefixedId('fee'),
    label: typeof row?.label === 'string' ? row.label.trim() : '',
    amountUsd: row?.amountUsd,
    code: row?.code || null,
    ...(row?.note ? {note: row.note} : {}),
  }))
}

/**
 * Resolve the contract-fees object from input + contract.standardFees
 * defaults. Mirrors the customer-side convention of named USD slots
 * (`assayFeeUsd`, `treatmentFeeUsd`) rather than a generic line-items array.
 */
const resolveOtherFeesForSettlement = (contract, input) => {
  const rows = resolveOtherFeesWithLegacy(contract).map((row) => cloneDeep(row))
  const supplied = (input && input.contractFees && input.contractFees.otherFees) || []
  const includedByCode = {}
  for (const row of supplied) {
    if (row && row.code) includedByCode[row.code] = row
  }
  const otherFeeIncluded = (input && input.otherFeeIncluded) || {}
  return rows.map((fee) => {
    const suppliedRow = includedByCode[fee.code] || {}
    const included = fee.conditional === true
      ? (otherFeeIncluded[fee.code] === true || suppliedRow.included === true)
      : true
    return {...fee, ...suppliedRow, included}
  })
}

const resolveContractFees = (input, contract) => {
  const supplied = (input && input.contractFees) || {}
  const contractAssay = resolveAssayFeeFromContract(contract)
  const paidMetals = Array.isArray(contract?.paidMetals) ? contract.paidMetals : []
  const suppliedAssay = supplied.perMetalAssayFee || {}
  const assayOptIn = (input && input.assayOptIn) || {}
  const perMetalAssayFee = {}
  for (const metal of paidMetals) {
    const metalKey = String(metal).toLowerCase()
    const contractRow = contractAssay[metalKey] || {feeUsd: 0}
    const suppliedRow = suppliedAssay[metalKey] || {}
    const feeUsd = Number.isFinite(suppliedRow.feeUsd) ? suppliedRow.feeUsd : contractRow.feeUsd
    const requiresOptIn = Boolean(contractRow.optInAtSalesOrder)
    const entry = {feeUsd}
    if (typeof suppliedRow.optedIn === 'boolean') {
      entry.optedIn = suppliedRow.optedIn
    } else if (requiresOptIn) {
      entry.optedIn = assayOptIn[metalKey] === true
    }
    perMetalAssayFee[metalKey] = entry
  }
  return {
    perMetalAssayFee,
    otherFees: resolveOtherFeesForSettlement(contract, input),
  }
}

// Recompute at settlement time now that the operator's opt-in decisions are
// known; opted-out metals drop out of the SLA max so the ETA may shorten
// vs. the `received`-time conservative seed.
const patchSettledEtaFromSettlement = (masterLot, input) => {
  const contract = masterLot && masterLot.contract
  if (!contract) return masterLot
  const received = (masterLot.milestones || []).find((milestone) => milestone.kind === MILESTONE_KINDS.RECEIVED)
  const receivedAt = received && received.occurredAt
  if (!receivedAt) return masterLot
  const optInMap = buildAssayOptInMapFromSettlement(contract, input?.assayOptIn, input?.contractFees)
  // Thread the settlement through SLA recompute so hasImpurityCharges modifier can fire when ≥1 adHocFees row carries code: 'impurity_charge'.
  const settlementShape = {adHocFees: Array.isArray(input?.adHocFees) ? input.adHocFees : []}
  const businessDays = resolveMaxSettlementBusinessDays(contract, masterLot, optInMap, settlementShape)
  if (!Number.isFinite(businessDays) || businessDays <= 0) return masterLot
  return applySettledEta(masterLot, settlementEtaFromReceived(receivedAt, businessDays))
}

/**
 * Run the contract's per-metal numbers through `computeSettlementPreview`
 * (derives creditedFineToz + refiningFeeUsd via tier match; passes through
 * the operator-transcribed metalValueUsd), then aggregate USD totals via
 * `computeSettlementTotals` so the persisted payload carries both the
 * itemized breakdown and the lot-level summary the card renders.
 *
 * Documents are passed through verbatim — the dialog has already POSTed
 * bytes via the api seam.
 */
const buildSettlementPayload = (masterLot, input, recordedAt) => {
  const manifestSummary = buildManifestSummaryForVariance(masterLot)
  const previewByMetal = computeSettlementPreview(masterLot.contract, manifestSummary, {
    reportedGrossToz: input.reportedGrossToz,
    perMetal: input.perMetal || {},
  })
  const contractFees = resolveContractFees(input, masterLot.contract)
  const adHocFees = normalizeAdHocFees(input.adHocFees)
  // Pass contract so computeSettlementTotals can auto-derive qualifyingPurityPct from previewByMetal[tierQualifyingMetal].purityPct; otherwise purity-tiered otherFees rows resolve to $0 on persist and silently diverge from the read-side card.
  const totals = computeSettlementTotals({
    contract: masterLot.contract,
    perMetal: previewByMetal,
    contractFees,
    adHocFees,
    reportedGrossToz: input.reportedGrossToz,
  })
  const {dispositionMode, dispositionDetails} = normalizeDisposition(input)
  return {
    recordedAt,
    partnerReferenceNumber: input.partnerReferenceNumber || '',
    reportedGrossToz: input.reportedGrossToz,
    perMetal: previewByMetal,
    contractFees,
    adHocFees,
    ...totals,
    notes: input.notes || '',
    documents: cloneDeep(Array.isArray(input.documents) ? input.documents : []),
    dispositionMode,
    dispositionDetails,
  }
}

/**
 * Resolve persisted disposition fields. Cash invoice is the legacy / no-op
 * default; non-cash modes represent off-system metal disposition (credited
 * balance stays partner-side rather than being invoiced as USD). Force
 * `dispositionDetails` to `null` on cash-invoice persistence so stale
 * destination/note text from a non-cash mode the operator backed out of
 * cannot round-trip.
 */
const normalizeDisposition = (input) => {
  const isCashInvoice = isCashInvoiceDisposition(input.dispositionMode)
  const dispositionMode = isCashInvoice ? DISPOSITION_MODES.CASH_INVOICE : input.dispositionMode
  const hasDetailsObject = input.dispositionDetails && typeof input.dispositionDetails === 'object'
  const dispositionDetails = (isCashInvoice || !hasDetailsObject) ? null : cloneDeep(input.dispositionDetails)
  return {dispositionMode, dispositionDetails}
}
